/**
 * The three priorities as compact cards, and the tabs for the other views of the book.
 *
 * A card shows the priority, how many contacts hold it and what to do with them, in about the height
 * of two lines, so the list below starts near the top of the page. Choosing a card filters the list
 * to that priority; choosing it again shows them all. The rule behind a priority appears when the
 * card is pointed at or focused, so "what makes someone High priority?" has an answer without leaving
 * the list.
 *
 * Two things a count alone would hide are said in a line under the cards: why High priority is empty,
 * and that Medium holds contacts moved out of High by the advisor's limit. When the list was last
 * scored sits at the end of the tab row, where there is room for it.
 *
 * The component keeps its name from the scoring service's word for a priority, a tier.
 */
import { Tabs, TabsList, TabsTrigger, Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import type { Prospecting } from '@client/api/types';
import { crmCopy } from '../lib/crm-copy';
import { formatDateTime, priorityCopy } from '../lib/labels';
import { PriorityBadge } from './priority-badge';

interface TierSummaryProps {
  book: Prospecting.BookStatus;
  view: Prospecting.ProspectView;
  selectedTier: string | undefined;
  onViewChange: (view: Prospecting.ProspectView) => void;
  onTierSelect: (tier: string | undefined) => void;
}

const TIERS = ['tier1', 'tier2', 'tier3'] as const;

const count = (value: number) => value.toLocaleString('en-US');

export function TierSummary({ book, view, selectedTier, onViewChange, onTierSelect }: TierSummaryProps) {
  const { counts } = book;
  const copy = crmCopy(book.provider);
  const noneCanBeHigh = book.confidence.reasons.includes('noRelationshipSource');
  const tiered = TIERS.reduce((sum, tier) => sum + (counts.tiers[tier] ?? 0), 0);
  const excluded = Object.values(counts.excluded).reduce((sum, n) => sum + n, 0);
  const untiered = Math.max(0, (counts.tiers.untiered ?? 0) - counts.insufficientData);
  const highIsEmpty = (counts.tiers.tier1 ?? 0) === 0 && !!book.scoring.lastScoredAt;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {TIERS.map(tier => {
          const selected = view === 'tiered' && selectedTier === tier;
          return (
            <Tooltip key={tier}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onTierSelect(selected ? undefined : tier)}
                  className="hover:border-primary/50 hover:bg-primary/5 focus-visible:ring-primary aria-pressed:border-primary aria-pressed:bg-primary/5 rounded-lg border border-gray-200 bg-white px-4 py-3 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
                >
                  <span className="flex items-center justify-between gap-3">
                    <PriorityBadge tier={tier} />
                    <span className="text-xl font-semibold text-gray-900 tabular-nums">
                      {count(counts.tiers[tier] ?? 0)}
                    </span>
                  </span>
                  <span className="mt-1 block text-xs text-gray-600">{book.tierActions[tier]}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{priorityCopy.rule(tier, copy.parentRecorded)}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>

      {(highIsEmpty || counts.cappedFromTier1 > 0) && (
        <p className="text-xs text-gray-600">
          {highIsEmpty &&
            (noneCanBeHigh
              ? priorityCopy.noneCanBeHigh(copy.name)
              : priorityCopy.highNeedsParent(copy.parentRecorded, copy.name))}
          {highIsEmpty && counts.cappedFromTier1 > 0 && ' '}
          {counts.cappedFromTier1 > 0 && priorityCopy.includesMoved(counts.cappedFromTier1)}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <Tabs
        value={view}
        onValueChange={value => onViewChange(value as Prospecting.ProspectView)}
      >
        <TabsList>
          <TabsTrigger value="tiered">
            {priorityCopy.tabs.tiered} ({count(tiered)})
          </TabsTrigger>
          <TabsTrigger value="untiered">
            {priorityCopy.tabs.untiered} ({count(untiered)})
          </TabsTrigger>
          <TabsTrigger value="insufficientData">
            {priorityCopy.tabs.insufficientData} ({count(counts.insufficientData)})
          </TabsTrigger>
          <TabsTrigger value="excluded">
            {priorityCopy.tabs.excluded} ({count(excluded)})
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {book.scoring.lastScoredAt && (
        <span className="text-xs text-gray-600">Last scored {formatDateTime(book.scoring.lastScoredAt)}</span>
      )}
      </div>
    </div>
  );
}
